<template>
	<div>
		<v-data-table
			v-if="mappedLocations"
			dense
			hide-default-header
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:loading="loading"
			:items="groupedLocations"
			item-key="name"
			single-expand
			:height="pageHeight(174)"
			:items-per-page="-1"
			:footer-props="getDataTableFooterProps('officesTable')">
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td class="text-left">
						<v-icon small color="primary" class="mr-1">
							{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
						</v-icon>
						{{ item.name }}
						<v-chip pill x-small color="primary" class="mx-2" label>
							{{ item.locations.length }}
							{{ item.locations.length > 1 ? $t('locations') : $t('location') }}
						</v-chip>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3">
						<v-data-table
							:items="item.locations"
							:headers="headers"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(84)"
							fixed-header>
							<template #item="{ item, index }">
								<tr @click="editLocationMapping(item)" :class="[getRowsForDataTable(index)]" class="text-left">
									<td>{{ item.ecpLocationName }}</td>
									<td>{{ item.hospital?.hospitalName }}</td>
									<td>{{ item.ecpLocationId }}</td>
									<td class="text-right">
										<v-menu left bottom class="py-0" offset-y transition="slide-x-transition">
											<template #activator="{ on, attrs }">
												<v-btn icon color="main_black" v-bind="attrs" v-on="on">
													<v-icon>mdi-dots-horizontal</v-icon>
												</v-btn>
											</template>
											<v-list class="px-0" dense>
												<v-list-item class="py-0 my-0" link dense @click="editLocationMapping(item)">
													<v-list-item-icon class="mx-1">
														<v-icon class="icon-img mdi-18px" color="black">mdi-eye</v-icon>
													</v-list-item-icon>
													<v-list-item-content class="font-bold font-size14">
														{{ $t('view') }}
													</v-list-item-content>
												</v-list-item>
												<v-divider />
												<v-list-item link dense @click="openDialog(item)">
													<v-list-item-icon class="mx-1">
														<v-icon v-if="roleName === 'SYSTEM_ADMIN'" color="main_red">mdi-trash-can-outline</v-icon>
													</v-list-item-icon>
													<v-list-item-content class="font-bold font-size14">
														{{ $t('delete') }}
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table>

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteEcpMapForLocation();
			" />
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import MappingRepository from '@/data/repositories/v1/MappingRepository.js';
import { getDataTableFooterProps, getRowsForDataTable } from '@/utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';

export default {
	data () {
		return {
			dialog: false,
			showDeleteDialog: false,
			patientToDelete: null,
			loading: false,
			officeId: null,
			mappedLocations: [],
		};
	},
	components: { DeleteDialog },
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			selectedLocationId: (state) => state.filterbar.hospitalId,
			medicalInstitution: (state) => state.filterbar.medicalInstitution,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			userSettings: 'authentication/getUserSettings',
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
		}),
		detailsDrawer () {
			return this.$store.state.detailsDrawer;
		},
		headers () {
			return [
				{ text: this.$t('ecp-location-name'), value: 'ecpLocationName' },
				{ text: this.$t('mapped-to'), value: 'hospital.hospitalName' },
				{ text: 'GUID', value: 'ecpLocationId' },
				{ text: '', value: 'actions' },
			];
		},
		groupedLocations () {
			const search = this.appBarSearch?.toLowerCase();
			return this.mappedLocations.reduce((groups, org) => {
				if (search) {
					const institutionName = org.medicalInstitution.institutionName.toLowerCase();
					const name = org.ecpLocationName.toLowerCase();
					const ecpLocationId = org.ecpLocationId.toLowerCase();
					const hospitalName = org.hospital?.hospitalName.toLowerCase();
					if (
						!institutionName.includes(search) &&
						!name.includes(search) &&
						!ecpLocationId.includes(search) &&
						!hospitalName.includes(search)
					) {
						return groups;
					}
				}
				let group = groups.find((g) => g.name === org.medicalInstitution.institutionName);
				if (!group) {
					group = { name: org.medicalInstitution.institutionName, locations: [] };
					groups.push(group);
				}
				group.locations.push(org);
				return groups;
			}, []);
		},
	},
	watch: {
		showDeleteDialog (val) {
			if (val === false) {
				this.getEcpMappingLocation();
			}
		},
		detailsDrawer (val) {
			if (val === false) {
				this.getEcpMappingLocation();
			}
		},
	},
	methods: {
		openDialog (item) {
			this.showDeleteDialog = true;
			this.officeId = item.id;
		},
		...mapActions({}),
		getRowsForDataTable,
		getDataTableFooterProps,
		openDeleteDialog (item) {
			this.showDeleteDialog = true;
			this.patientToDelete = item.id;
		},
		async editLocationMapping (item) {
			await this.$store.commit('mapping/SELECTED_LOCATION', item);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
		},
		getEcpMappingLocation () {
			this.loading = true;
			MappingRepository.getMappedEcpLocation().then((res) => {
				this.mappedLocations = res;
				this.$store.commit('mapping/SET_ECP_LOCATIONS', res);
				this.loading = false;
			});
		},
		async deleteEcpMapForLocation () {
			MappingRepository.deleteEcpMapForLocation(this.officeId).then((res) => {
				this.getEcpMappingLocation();
			});
		},
	},
	async mounted () {
		this.getEcpMappingLocation();
	},
};
</script>

<style>
.v-messages {
	display: none !important;
}

.small i {
	font-size: 18px !important;
}

.small [class*='__ripple'] {
	left: 0;
	display: none;
}
</style>
